/**
 * FixkostenCard (Phase 6, §6.3) — a Budget-page summary of the month's
 * recurring-payment forecast (`GET /api/contracts/forecast`), styled like a
 * `BudgetGroupSection` (label left, tabular-nums figure right). The whole
 * card links to `/vertraege`; `MonthViewOut` stays untouched — this is a
 * separate query composed client-side.
 */
import { Link } from 'react-router';
import { money } from '../../lib/format.ts';
import { useContractForecast } from '../../lib/hooks/useContracts.ts';
import type { MonthStr } from '../../lib/types.ts';

export interface FixkostenCardProps {
  month: MonthStr;
}

export function FixkostenCard({ month }: FixkostenCardProps) {
  const { data, isLoading } = useContractForecast(month);

  if (isLoading || !data) return null;

  const { totals, rows } = data;
  const allZero = Object.values(totals).every((v) => Number(v) === 0);
  if (rows.length === 0 && allZero) return null;

  const warnCount = totals.overdue_count + totals.drift_count;
  const abs = (v: string) => money(Math.abs(Number(v)));

  return (
    <Link
      to="/vertraege"
      className="block rounded-lg border border-slate-200 bg-white px-4 py-3.5 transition-colors duration-150 hover:border-slate-300 dark:border-slate-800 dark:bg-slate-900 dark:hover:border-slate-700"
    >
      <header className="flex items-center justify-between gap-3 border-b border-slate-100 pb-2.5 dark:border-slate-800/70">
        <h2 className="text-xs font-semibold uppercase tracking-[0.06em] text-slate-400 dark:text-slate-500">
          Fixkosten
        </h2>
        <span className="text-xs text-slate-500 tabular-nums dark:text-slate-400">
          {abs(totals.booked_out)} / {abs(totals.expected_out)}
        </span>
      </header>
      <p className="pt-2.5 text-sm text-slate-600 dark:text-slate-300">
        Fixkosten · erwartet {abs(totals.expected_out)} · gebucht {abs(totals.booked_out)} · offen{' '}
        {abs(totals.open_out)}
      </p>
      {Number(totals.expected_in) !== 0 ? (
        <p className="pt-1 text-sm text-slate-600 dark:text-slate-300">
          Erwartete Einnahmen {abs(totals.expected_in)}
        </p>
      ) : null}
      {warnCount > 0 ? (
        <span className="mt-2 inline-flex items-center rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-950 dark:text-amber-300">
          {warnCount} Auffälligkeit(en)
        </span>
      ) : null}
    </Link>
  );
}
